/**
 * Color Shift mutation — fractures the dominant color group.
 *
 * DESIGN NOTE: the original "merge X → Y" version helped the player (bigger
 * clusters, easier matches, smaller palette to juggle) instead of hurting
 * them. This version inverts the intent: it finds one of the two most-
 * numerous colors on the board and repaints a fraction of THAT color's
 * gears into random other active colors — speckling any coherent cluster
 * the player had been building around.
 *
 * Properties:
 *   - No gears added or removed (never overflow-kills, never gives free space)
 *   - Only touches a dominant color: a well-balanced board suffers less
 *   - Bounded blast radius (SHATTER_FRACTION) so the run isn't unwinnable
 *
 * MIRRORED in supabase/functions/_shared/engine-replay.ts — keep in sync.
 */

import type { PRNG } from '../../rng.js';
import type { BoardState, MutationCard, Bubble, BubbleColor } from '../../types.js';
import { ALL_BUBBLE_COLORS } from '../../types.js';

/** Fraction of the chosen color's gears repainted into other colors. */
const SHATTER_FRACTION = 0.4;
/** Minimum gears repainted (guards small boards from a no-op single flip). */
const MIN_SHATTER = 2;

export function applyColorShift(
  board: BoardState,
  _card: MutationCard,
  rng: PRNG,
): BoardState {
  if (board.activeColors.length < 2) return board;
  if (board.bubbles.size === 0) return board;

  // Tally non-obstacle bubbles by color; only count colors that are still in
  // the active palette (a legacy color from before an activeColors change
  // shouldn't be a target — repainting it can't tune the current game state).
  const counts = new Map<BubbleColor, number>();
  for (const [, bubble] of board.bubbles) {
    if (bubble.isObstacle) continue;
    if (!board.activeColors.includes(bubble.color)) continue;
    counts.set(bubble.color, (counts.get(bubble.color) ?? 0) + 1);
  }
  if (counts.size === 0) return board;

  // Rank colors by count desc, tiebreak by ALL_BUBBLE_COLORS enum order so
  // both the client and the server-side replay pick identically.
  const ranked = [...counts.entries()]
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return ALL_BUBBLE_COLORS.indexOf(a[0]) - ALL_BUBBLE_COLORS.indexOf(b[0]);
    })
    .map(([c]) => c);

  // Choose from the top two dominant colors (or the sole color if only one
  // is on the board). Randomizing the pick keeps the effect from being
  // perfectly predictable while still targeting the player's biggest asset.
  const topPool = ranked.slice(0, Math.min(2, ranked.length));
  const sourceColor = rng.pick(topPool);

  const otherColors = board.activeColors.filter(c => c !== sourceColor);
  if (otherColors.length === 0) return board;

  // Collect the source-color coords deterministically so client + server
  // sample the same pool from the same RNG stream.
  const sourceKeys: string[] = [];
  for (const [key, bubble] of board.bubbles) {
    if (bubble.color === sourceColor && !bubble.isObstacle) {
      sourceKeys.push(key);
    }
  }
  if (sourceKeys.length === 0) return board;
  sourceKeys.sort();

  const shatterCount = Math.min(
    sourceKeys.length,
    Math.max(MIN_SHATTER, Math.ceil(sourceKeys.length * SHATTER_FRACTION)),
  );

  // Fisher-Yates prefix: pick `shatterCount` distinct keys without shifting
  // an Array each time (avoids the O(n) splice from an earlier draft).
  const pool = [...sourceKeys];
  const picked = new Set<string>();
  for (let i = 0; i < shatterCount; i++) {
    const j = i + rng.nextInt(0, pool.length - 1 - i);
    const tmp = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = tmp;
    picked.add(pool[i]!);
  }

  const newBubbles = new Map<string, Bubble>();
  for (const [key, bubble] of board.bubbles) {
    if (picked.has(key)) {
      const newColor = rng.pick(otherColors);
      newBubbles.set(key, { ...bubble, color: newColor });
    } else {
      newBubbles.set(key, bubble);
    }
  }

  return { ...board, bubbles: newBubbles };
}
